import type { SsoProviderKey } from '@/lib/auth/ssoProviders'

export const TRACKED_LINK_CONFIRMATION_ENDPOINT = 'confirmTrackedLinks' as const

export const apiEndpoints = {
    // User Entity
    user: {
        getProfile: '/api/external/patient',
        updateProfile: '/api/external/patient',
        getPrimaryCareProvider: '/api/external/patient/primary_care_provider',
        getMercyBilling: '/api/external/patient/mercy_billing',
        /** Trust Commerce return callback (GET). */
        updateMercyBilling: '/api/external/patient/update_mercy_billing',
        getPaymentMethods: '/api/external/patients/payment_methods',
        deletePaymentMethod: (id: string) => `/api/external/patients/payment_methods/${id}`,
        updateDefaultPaymentMethod: (patientId: string) =>
            `/api/external/patients/${patientId}/update_default_payment_method`,
        getSsoStatus: (provider: SsoProviderKey) => `/api/external/${provider}_sso`,
        startSsoOAuth: (provider: SsoProviderKey) => `/api/external/${provider}_sso/oauth/start`,
        disconnectSso: (provider: SsoProviderKey) => `/api/external/${provider}_sso/disconnect`,
        getPasskeys: '/api/external/passkeys',
        startPasskeyRegistration: '/api/external/passkeys/registration/start',
        completePasskeyRegistration: '/api/external/passkeys/registration/complete',
        deletePasskey: (credentialId: string) => `/api/external/passkeys/${encodeURIComponent(credentialId)}`,
        revokeAllSessions: '/api/external/session_revocations',
        getSignInMethodEnrollment: '/api/external/sign_in_methods/enrollment',
    },

    emergencyContacts: {
        list: '/api/external/patient/emergency_contacts',
    },

    insurances: {
        getInsurances: '/api/external/insurances',
        deleteInsurance: (id: string) => `/api/external/insurances/${id}`,
        getInsurancePolicy: '/api/external/insurances/show',
    },

    insuranceOnboarding: {
        discoverInsurance: '/api/external/visits/insurance_for_discovery_confirmation',
        directoryPayers: '/api/external/insurances/directory_payers',
        enqueueEligibilityCheck: '/api/external/insurances/enqueue_eligibility_check',
        triggerEligibilityCheck: '/api/external/insurances/trigger_eligibility_check',
        triggerCardImageEligibilityCheck: '/api/external/insurances/trigger_card_image_eligibility_check',
        activate: (insuranceId: string) => `/api/external/insurances/${insuranceId}/activate`,
        getById: (onboardingId: string) => `/api/external/insurance_onboardings/${onboardingId}`,
    },

    // Dashboard Entity (Aggregated Data)
    dashboard: {
        getOverview: '/api/external/dashboard/show',
        getCarePlans: '/api/external/dashboard/care_plans',
        getPrescriptions: '/api/external/dashboard/prescriptions',
        getNextStepCards: '/api/external/dashboard/next_step_cards',
        getPreVisitRequirements: '/api/external/dashboard/pre_visit_requirements',
        dismissNextStepsIntro: '/api/external/dashboard/next_step_cards/dismiss_intro_notice',
        snoozeNextStepCard: '/api/external/dashboard/next_step_cards/snooze',
        dismissNextStepCard: '/api/external/dashboard/next_step_cards/dismiss',
        getLabOrders: '/api/external/dashboard/lab_orders',
        getVisits: '/api/external/dashboard/visits',
        getPastProviders: '/api/external/dashboard/past_providers',
    },

    // Updates Entity (notification center feed)
    updates: {
        list: '/api/external/updates',
        unreadCount: '/api/external/updates/unread_count',
        read: '/api/external/updates/read',
        readAll: '/api/external/updates/read_all',
    },

    messageCenter: {
        conversations: '/api/external/conversations',
        unreadCount: '/api/external/conversations/unread_count',
        conversation: (conversationId: string) => `/api/external/conversations/${conversationId}`,
        conversationMessages: (conversationId: string) => `/api/external/conversations/${conversationId}/messages`,
        conversationResponseExpectation: (conversationId: string) =>
            `/api/external/conversations/${conversationId}/response_expectation`,
        newConversationResponseExpectation: '/api/external/response_expectation',
        createToken: '/api/external/conversations/token',
        conversationSubjects: '/api/external/conversation_subjects',
        conversationForSubject: '/api/external/conversations/for_subject',
        search: '/api/external/conversations/search',
    },

    // My Care inventory (the patient's chronological list of care objects)
    myCare: {
        list: '/api/external/my_care',
    },

    // Account dashboard intro modal (one-time, permanent dismissal)
    dashboardIntro: {
        getStatus: '/api/external/dashboard_intro/status',
        acknowledge: '/api/external/dashboard_intro/acknowledge',
    },

    // Visits Entity
    visits: {
        getById: (visitId: string) => `/api/external/visits/${visitId}`,
        create: '/api/external/visits/create',
        getPayment: (visitId: string) => `/api/external/visits/${visitId}/payment`,
        bookingSelection: (visitId: string) => `/api/external/visits/${visitId}/booking_selection`,
        getOptions: (visitId: string) => `/api/external/visits/${visitId}/options`,
        /**
         * Nested PCT + ANY_PROVIDER when the visit FOI has a selected offer
         * (same shape as IPV `referralOrders.offerProviders`).
         */
        offerProviders: (visitId: string) => `/api/external/visits/${visitId}/providers`,
        getTopics: (visitId: string) => `/api/external/visits/${visitId}/topics`,
        addTopic: (visitId?: string) => `/api/external/visits/${visitId || 'nil'}/add_topic`,
        removeTopic: (visitId?: string) => `/api/external/visits/${visitId || 'nil'}/remove_topic`,
        setPaymentMethod: (visitId: string) => `/api/external/visits/${visitId}/payment_method`,
        statusUpdate: (visitId: string) => `/api/external/visits/${visitId}/status_update`,
        checkInSteps: (visitId: string) => `/api/external/visits/${visitId}/check_in_steps`,
        book: '/api/external/visits/book',
    },

    // Product Detail Pages (medication PDPs)
    productDetailPages: {
        getBySkuId: (skuId: string) => `/api/external/product_detail_pages/${skuId}`,
    },

    // Public pharmacy directory detail pages (Store 2.0)
    pharmacyDetailPages: {
        show: (friendlyId: string) => `/api/external/pharmacy_detail_pages/${encodeURIComponent(friendlyId)}`,
        medications: (friendlyId: string, page = 1, perPage = 50) => {
            const searchParams = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
            })
            return `/api/external/pharmacy_detail_pages/${encodeURIComponent(friendlyId)}/medications?${searchParams.toString()}`
        },
    },

    // Medication-option SKU pricing (the orderable GPI-14 dose/form/strength backing a PDP)
    medicationOptionSkus: {
        getCashPrice: (skuId: string, zipCode?: string) =>
            zipCode
                ? `/api/external/medication_option_skus/${skuId}/cash_price?zip_code=${encodeURIComponent(zipCode)}`
                : `/api/external/medication_option_skus/${skuId}/cash_price`,
    },

    // Public, chain-deduped nearby-pharmacy comparison for the medication PDP
    pharmacyComparisons: {
        list: (skuId: string, latitude: number, longitude: number) =>
            `/api/external/pharmacy_comparisons?medication_option_sku_id=${skuId}&latitude=${latitude}&longitude=${longitude}`,
    },

    // SKUs Entity (Products/Services)
    skus: {
        getById: (skuId: string) => `/api/external/skus/${skuId}`,
        getOptions: (skuId: string) => `/api/external/sku_options/${skuId}`,
        getOptionsWithNextStep: (skuId: string, nextStepId: string) =>
            `/api/external/sku_options/${skuId}?next_step_id=${nextStepId}`,
        getOptionsForLocationState: (skuId: string, appointmentLocationState?: string | null) => {
            if (!appointmentLocationState) return `/api/external/sku_options/${skuId}`
            const query = new URLSearchParams({ appointment_location_state: appointmentLocationState })
            return `/api/external/sku_options/${skuId}?${query}`
        },
        getOptionsWithRenewalContext: (skuId: string, renewalQuestionnaireResponseId: string, nextStepId?: string) => {
            const query = new URLSearchParams({
                renewal_questionnaire_response_id: renewalQuestionnaireResponseId,
            })
            if (nextStepId) query.set('next_step_id', nextStepId)
            return `/api/external/sku_options/${skuId}?${query}`
        },
        getPrices: (skuIds: string[]) =>
            `/api/external/sku_options/prices?${skuIds.map((id) => `sku_ids[]=${encodeURIComponent(id)}`).join('&')}`,
        getOffers: (
            skuId: string,
            options?: {
                fulfillmentOwnerId?: string | null
                screeningQuestionnaireResponseId?: string | null
            },
        ) => {
            const query = new URLSearchParams()
            if (options?.fulfillmentOwnerId) {
                query.set('fulfillment_owner_id', options.fulfillmentOwnerId)
            }
            const screeningQuestionnaireResponseId = options?.screeningQuestionnaireResponseId?.trim()
            if (screeningQuestionnaireResponseId) {
                query.set('screening_questionnaire_response_id', screeningQuestionnaireResponseId)
            }
            const qs = query.toString()
            return qs ? `/api/external/skus/${skuId}/offers?${qs}` : `/api/external/skus/${skuId}/offers`
        },
        checkStateAvailability: (skuId: string, state: string) =>
            `/api/external/sku_state_settings/check?${new URLSearchParams({ sku_id: skuId, state })}`,
    },

    // Category Entity (Store categories / rendered services)
    category: {
        getBySlug: (slug: string) => `/api/external/category/${encodeURIComponent(slug)}`,
    },

    /** Universal storefront search (`SearchResultSerializer` index). */
    storeSearch: {
        index: (params: {
            query: string
            includeProviders?: boolean
            includePharmacies?: boolean
            zipcode?: string | null
            latitude?: number
            longitude?: number
            skuCategory?: string
        }) => {
            const searchParams = new URLSearchParams()
            searchParams.set('query', params.query)
            if (params.includeProviders) {
                searchParams.set('include_providers', 'true')
            }
            if (params.includePharmacies) {
                searchParams.set('include_pharmacies', 'true')
            }
            if (params.zipcode) {
                searchParams.set('zipcode', params.zipcode)
            }
            if (params.latitude != null) {
                searchParams.set('latitude', String(params.latitude))
            }
            if (params.longitude != null) {
                searchParams.set('longitude', String(params.longitude))
            }
            if (params.skuCategory) {
                searchParams.set('sku_category', params.skuCategory)
            }
            return `/api/external/search/index?${searchParams.toString()}`
        },
        /** SKU-only search for legacy `/store/search` UI (no provider params). */
        legacyIndex: (query: string) => `/api/external/search/index?query=${encodeURIComponent(query)}`,
        suggestions: (query: string) => `/api/external/search/suggestions?query=${encodeURIComponent(query)}`,
    },

    // Care Entity
    care: {
        getById: (careId: string) => `/api/external/cares/${careId}`,
        getNextSteps: (careId: string) => `/api/external/next_steps/by_care/${careId}`,
        getActions: '/api/external/care_actions',
        getNextStepPrescriptionRenewal: (nextStepId: string) =>
            `/api/external/next_step_prescription_renewals/${nextStepId}`,
        getPrescriptionRenewalDestination: '/api/external/prescription_renewal_destination',
        declineNextStep: (id: string) => `/api/external/next_step_prescription_renewals/${id}/decline`,
        completedElsewhereNextStep: (id: string) =>
            `/api/external/next_step_prescription_renewals/${id}/completed_elsewhere`,
    },

    careJourney: {
        nextSteps: '/api/external/care_journey/next_steps',
        labBundle: '/api/external/care_journey/lab_bundle',
        completeLabUpload: '/api/external/care_journey/complete_lab_upload',
        shareLabUploadForBooking: '/api/external/care_journey/share_lab_upload_for_booking',
    },

    carePlans: {
        getConditionGroups: '/api/external/care_plans/condition_groups',
    },

    orders: {
        list: '/api/external/orders',
    },

    fileUploads: {
        list: '/api/external/file_uploads',
        create: '/api/external/file_uploads',
        presign: '/api/external/file_uploads/presign',
        complete: (id: string) => `/api/external/file_uploads/${encodeURIComponent(id)}/complete`,
        delete: (id: string) => `/api/external/file_uploads/${encodeURIComponent(id)}`,
        download: (id: string) => `/api/external/file_uploads/${encodeURIComponent(id)}/download`,
        status: (id: string) => `/api/external/file_uploads/${encodeURIComponent(id)}/status`,
    },

    // Prescriptions Entity
    prescriptions: {
        ensurePharmacyOrder: (careActionPrescriptionId: string) =>
            `/api/external/prescriptions/${encodeURIComponent(careActionPrescriptionId)}/ensure_pharmacy_order`,
        getById: (id: string) => `/api/external/prescriptions/${id}`,
    },

    // Patient Medications Entity (new prescription detail shape)
    patientMedications: {
        getById: (id: string) => `/api/external/patient_medications/${encodeURIComponent(id)}`,
        labBundle: (id: string) => `/api/external/patient_medications/${encodeURIComponent(id)}/lab_bundle`,
        renewable: '/api/external/patient_medications/renewable',
    },

    // Lab Orders Entity
    labOrders: {
        getById: (labOrderId: string) => `/api/external/lab_orders/${labOrderId}`,
    },

    // Questions/Questionnaires Entity
    questionnaires: {
        getResponses: (skuId: string, questionId: string) =>
            `/api/external/question_responses?sku_id=${skuId}&question_id=${questionId}`,
        getQuestion: (questionnaireId: string, questionNumber: string) =>
            `/api/external/questions/${questionnaireId}/${questionNumber}`,
    },

    // Questionnaire Responses Entity (completed questionnaire submissions)
    questionnaireResponses: {
        markRead: (questionnaireResponseId: string) =>
            `/api/external/questionnaire_responses/${encodeURIComponent(questionnaireResponseId)}/read`,
    },

    // Health History Entity
    healthHistory: {
        lookupExists: '/api/external/health_history/lookup_exists',
        getLatestMetriport: '/api/external/health_history/latest_metriport',
        getCopilotStatus: '/api/external/health_history/copilot_health_history_status',
        submitReview: '/api/external/health_history/submit_review',
    },

    personalInformation: {
        base: '/api/external/patient/personal_information',
    },

    // Providers Entity
    providers: {
        getById: (providerId: string) => `/api/external/providers/${providerId}`,
        search: '/api/external/providers/search',
        searchSuggestions: '/api/external/providers/search/suggestions',
        directoryProviderShow: (slug: string, includePricing = true, zipCode?: string) => {
            const searchParams = new URLSearchParams()
            if (includePricing) searchParams.set('include_pricing', 'true')
            if (zipCode) searchParams.set('zip_code', zipCode)
            const query = searchParams.toString()
            return `/api/external/directory_providers/${encodeURIComponent(slug)}${query ? `?${query}` : ''}`
        },
    },

    // Analytics Entity
    analytics: {
        visits: '/api/external/analytics/visits',
        events: '/api/external/analytics/events',
    },

    // Campaign touch (multi-touch attribution). Posts to the same-origin proxy
    // route, which injects the server attestation token before forwarding to
    // Nile-Core.
    campaignTouches: {
        create: '/api/campaign-touches',
    },

    // Local confirmation route. The HttpOnly pending cookie is read server-side
    // and forwarded to Nile-Core as `{ tracked_link_codes: string[] }`.
    trackedLinks: {
        confirm: '/api/tracked-links/confirm',
    },

    // Copilot endpoints
    copilot: {
        getChatHistory: '/api/external/copilot/messages',
        createMessage: '/api/external/copilot/messages',
        messageReceived: '/api/external/copilot/message_received',
        getMessageProcessingLocked: '/api/external/copilot/message_processing_locked',
        sessionExists: '/api/external/copilot/session_exists',
        consentStatus: '/api/external/copilot/consent/status',
        markConsent: '/api/external/copilot/consent/mark',
        loadingIndicator: '/api/external/copilot/loading_indicator',
    },

    payment: {
        createSetupIntent: (patientId: string) => `/api/external/patients/${patientId}/create_stripe_setup_intent`,
        fetchPaymentMethod: (patientId: string, paymentMethodId?: string) =>
            paymentMethodId
                ? `/api/external/patients/${patientId}/fetch_payment_method?payment_method_id=${encodeURIComponent(
                      paymentMethodId,
                  )}`
                : `/api/external/patients/${patientId}/fetch_payment_method`,
    },
    emailValidation: {
        validate: '/api/external/email_validation/validate',
    },
    // Geocoding
    geocoding: {
        reverse: '/api/external/geocoding/reverse',
        forward: '/api/external/geocoding/forward',
    },
    // Pharmacy Search
    pharmacySearch: {
        search: '/api/external/pharmacy_search',
    },
    preferredPharmacy: '/api/external/patient/preferred_pharmacy',
    // Consent documents the patient signs at check-in for their fulfillment owner
    consents: {
        required: '/api/external/patient/consents/required',
        docusignUrl: '/api/external/patient/consents/docusign_url',
    },
    pharmacyDetail: {
        detail: (ncpdpProviderId: string) => `/api/external/pharmacy_detail/${encodeURIComponent(ncpdpProviderId)}`,
    },
    pharmacyPricing: {
        insurance: '/api/external/pharmacy_pricing/insurance',
        insuranceStatus: (inquiryId: string) => `/api/external/pharmacy_pricing/insurance/${inquiryId}`,
        cashPrice: '/api/external/pharmacy_pricing/cash_price',
    },

    // Care Actions Entity
    careActions: {
        list: '/api/external/care_actions',
        detail: (id: string) => `/api/external/care_actions/${id}`,
        snooze: (id: string) => `/api/external/care_actions/${id}/snooze`,
        decline: (id: string) => `/api/external/care_actions/${id}/decline`,
        bookedMyself: (id: string) => `/api/external/care_actions/${id}/booked_myself`,
        autoBook: (id: string) => `/api/external/care_actions/${id}/auto_book`,
        pdf: (id: string) => `/api/external/care_actions/${id}/pdf`,
        providers: (id: string) => `/api/external/care_actions/${id}/providers`,
        providerDetail: (refId: string, provId: string) => `/api/external/care_actions/${refId}/providers/${provId}`,
        chooseProvider: (id: string) => `/api/external/care_actions/${id}/choose_provider`,
        addToCart: (id: string) => `/api/external/care_actions/${id}/add_to_cart`,
        removeFromCart: (id: string) => `/api/external/care_actions/${id}/remove_from_cart`,
        orderSummary: (id: string) => `/api/external/care_actions/${id}/order_summary`,
        checkout: (id: string) => `/api/external/care_actions/${id}/checkout`,
    },

    tenantIntegrations: {
        phoneNumber: '/api/external/tenant_integrations/phone_number',
    },

    // Referral Orders Entity
    partnerConsents: {
        get: '/api/partner/consents',
        revoke: (partnerFriendlyName: string) =>
            `/api/partner/consents/${encodeURIComponent(partnerFriendlyName)}/revoke`,
    },

    testSetup: {
        resetTestUser: '/api/external/test_setup/reset_test_user',
        deleteTestUser: '/api/external/test_setup/delete_test_user',
    },

    referralOrders: {
        list: '/api/external/referral/orders',
        detail: (id: string) => `/api/external/referral/orders/${id}`,
        preference: (orderId: string) => `/api/external/referral/orders/${orderId}/preference`,
        /** Flat FO list (VisitOffers-shaped); Core GET .../in_person_visit/orders/:id/offers */
        offers: (orderId: string) => `/api/external/in_person_visit/orders/${orderId}/offers`,
        /**
         * Get Providers for a specific offer attached to the order
         * Nested `availability_by_participant` 
         */
        offerProviders: (orderId: string) => `/api/external/in_person_visit/orders/${orderId}/providers`,
        providers: (orderId: string) => `/api/external/referral/orders/${orderId}/providers`,
        provider: (orderId: string, id: string) => `/api/external/referral/orders/${orderId}/providers/${id}`,
        providerSearch: (orderId: string) => `/api/external/referral/orders/${orderId}/providers/search`,
        providerSearchDetail: (orderId: string, id: string) =>
            `/api/external/referral/orders/${orderId}/providers/search/${id}`,
        providerPricing: (orderId: string) => `/api/external/in_person_visit/orders/${orderId}/providers/pricing`,
        orderPricing: (orderId: string) => `/api/external/in_person_visit/orders/${orderId}/pricing`,
        checkout: (orderId: string) => `/api/external/referral/orders/${orderId}/checkout`,
        payment: (orderId: string) => `/api/external/referral/orders/${orderId}/payment`,
    },

    urgentCare: {
        create: '/api/external/urgent_care/orders',
        detail: (orderId: string) => `/api/external/urgent_care/orders/${orderId}`,
        /** Flat FO list (VisitOffers-shaped); Core GET .../orders/:id/offers */
        offers: (orderId: string) => `/api/external/urgent_care/orders/${orderId}/offers`,
        /** Attach selected FO on an existing order (PATCH). */
        selectOffer: (orderId: string) => `/api/external/urgent_care/orders/${orderId}`,
        locations: (orderId: string) => `/api/external/urgent_care/orders/${orderId}/locations`,
        checkout: (orderId: string) => `/api/external/urgent_care/orders/${orderId}/checkout`,
        reschedule: (orderId: string) => `/api/external/urgent_care/orders/${orderId}/reschedule`,
    },
} as const

// Type helpers for better TypeScript support
export type ApiEndpoints = typeof apiEndpoints
export type UserEndpoints = typeof apiEndpoints.user
export type DashboardEndpoints = typeof apiEndpoints.dashboard
export type VisitsEndpoints = typeof apiEndpoints.visits
export type SkusEndpoints = typeof apiEndpoints.skus
export type CareEndpoints = typeof apiEndpoints.care
export type CareJourneyEndpoints = typeof apiEndpoints.careJourney
export type PrescriptionsEndpoints = typeof apiEndpoints.prescriptions
export type LabOrdersEndpoints = typeof apiEndpoints.labOrders
export type QuestionnairesEndpoints = typeof apiEndpoints.questionnaires
export type HealthHistoryEndpoints = typeof apiEndpoints.healthHistory
export type AnalyticsEndpoints = typeof apiEndpoints.analytics
export type CampaignTouchesEndpoints = typeof apiEndpoints.campaignTouches
export type CopilotEndpoints = typeof apiEndpoints.copilot
export type CareActionsEndpoints = typeof apiEndpoints.careActions
export type ReferralOrdersEndpoints = typeof apiEndpoints.referralOrders
